<script lang="ts">
    import { navigation } from '@otto-ec/global-resources/navigation';
    import { untrack } from 'svelte';
    import type { DresonRuleResolvedEvent } from '@shared/types/Tilelist.ts';
    import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
    import type { TileListLoadedPayload } from '@tilelist/application/ports/TileListLoaded.ts';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import { type NavigationCheckpoint, isNavigationCheckpoint } from './NavigationCheckpoint.ts';
    import { appendHeurekaFragment, clean, pagedUrl } from './Utils.browser.ts';

    const { initialSession }: { initialSession: TileListInitialSession } = $props();
    let { onex, errorReporting, browser: wiring } = $derived(injectWiring());

    function urlFor(event: DresonRuleResolvedEvent): string {
        const url = event.redirect ?? pagedUrl(clean(event.page), location.pathname, location.search);
        return event.redirect && onex.isToggleActive('DA_KEEP_HEUREKA_SHEET_FRAGMENT')
            ? appendHeurekaFragment(url)
            : url;
    }

    async function navigate(url: string, history: 'push' | 'replace', state: NavigationCheckpoint): Promise<boolean> {
        try {
            return await navigation.navigate(url, {
                history,
                state,
            });
        } catch (error: unknown) {
            errorReporting.report({ type: 'navigation-history', error });
            return false;
        }
    }

    async function handleLoaded({ event }: TileListLoadedPayload, isActive: () => boolean): Promise<void> {
        if (!isActive()) return;
        if (!wiring) return;
        const url = urlFor(event);
        if (event.historic) return;
        const navigated = await navigate(
            url,
            'push',
            wiring.createNavigationCheckpoint(event.rule, event.page, event.tsLink),
        );
        if (isActive() && navigated) wiring.trackingService?.trackUrl(event.redirect ?? url);
    }

    async function handleNavigation(
        event: Parameters<Parameters<typeof navigation.onNavigate.subscribe>[0]>[0],
        isActive: () => boolean,
    ): Promise<void> {
        if (!isActive()) return;
        if (!wiring) return;
        if (event.type !== 'popstate') return;
        const restored = event.destination.state;
        if (isNavigationCheckpoint(restored) && isActive()) {
            const { rule, entryRule, page, tsLink } = restored;
            wiring.eventsOutgoing?.requestPageResolution({
                rule,
                entryRule,
                page: { ...page },
                tsLink,
                historic: true,
            });
        }
    }

    function subscribe() {
        if (!wiring) return;
        let active = true;
        const isActive = () => active;
        let pending: Promise<void> = navigate(
            location.href,
            'replace',
            wiring.createNavigationCheckpoint(initialSession.page.context.rule, initialSession.page.page),
        ).then(() => undefined);
        const enqueue = (work: () => Promise<void>) => {
            pending = pending.then(work, work);
        };
        const unsubscribeLoaded = wiring.tileListLoaded.onTileListLoaded((payload) =>
            enqueue(() => handleLoaded(payload, isActive)),
        );
        const unsubscribeRestore = navigation.onNavigate.subscribe((event) =>
            enqueue(() => handleNavigation(event, isActive)),
        );
        return () => {
            active = false;
            unsubscribeLoaded();
            unsubscribeRestore();
        };
    }

    $effect(() => {
        if (!wiring) return;
        return untrack(subscribe);
    });
</script>
